import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { auth } from '@/lib/firebase'
import { API_BASE } from '@/services/api'

export interface UserSubscription {
  status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled'
  priceId?: string
  currentPeriodEnd?: number
}

export interface UserData {
  subscription?: UserSubscription
}

/**
 * Check if a user has an active subscription
 */
export const checkUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId))

    if (!userDoc.exists()) {
      return null
    }

    const userData = userDoc.data() as UserData
    return userData.subscription || null
  } catch (error) {
    console.error('Error checking user subscription:', error)
    return null
  }
}

/**
 * Check if a subscription is active (can access dashboard)
 */
export const isSubscriptionActive = (subscription: UserSubscription | null): boolean => {
  if (!subscription) return false

  // Check if subscription status is active or trialing
  if (subscription.status === 'active' || subscription.status === 'trialing') {
    // For subscriptions with currentPeriodEnd, check if it's not expired
    if (subscription.currentPeriodEnd) {
      return subscription.currentPeriodEnd * 1000 > Date.now()
    }
    return true
  }

  return false
}

/**
 * Cancel the user's subscription
 */
export const cancelSubscription = async (): Promise<void> => {
  const user = auth.currentUser
  if (!user) {
    throw new Error('No authenticated user')
  }

  const idToken = await user.getIdToken()
  const response = await fetch(`${API_BASE}/cancelSubscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to cancel subscription')
  }
}
